import * as XLSX from 'xlsx'
import type { MemberImportRowInput } from '@riverpark/types'
import { MEMBER_IMPORT_MAX_ROWS } from '@riverpark/types'
import {
  mapImportHeaderFields,
  MEMBER_IMPORT_SHEET_COLUMNS,
  MEMBER_IMPORT_TEMPLATE_EXAMPLE_ROW,
  normalizeKenyaPhone,
} from '@riverpark/utils/member-import'

export {
  MEMBER_IMPORT_SHEET_COLUMNS,
  MEMBER_IMPORT_TEMPLATE_EXAMPLE_ROW,
} from '@riverpark/utils/member-import'

export const IMPORT_TEMPLATE_HEADERS = MEMBER_IMPORT_SHEET_COLUMNS.map((c) => c.header)

function cellValue(row: unknown[], index: number | undefined): string {
  if (index === undefined) return ''
  const value = row[index]
  if (value == null) return ''
  return String(value).trim()
}

function escapeCsvField(value: string): string {
  if (/[",\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`
  }
  return value
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  anchor.click()
  URL.revokeObjectURL(url)
}

export function downloadImportTemplateCsv() {
  const lines = [
    IMPORT_TEMPLATE_HEADERS.map(escapeCsvField).join(','),
    MEMBER_IMPORT_TEMPLATE_EXAMPLE_ROW.map(escapeCsvField).join(','),
  ]
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' })
  downloadBlob(blob, 'riverpark-member-import-template.csv')
}

export function downloadImportTemplateExcel() {
  const sheet = XLSX.utils.aoa_to_sheet([
    IMPORT_TEMPLATE_HEADERS,
    MEMBER_IMPORT_TEMPLATE_EXAMPLE_ROW,
  ])
  const workbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(workbook, sheet, 'Members')
  XLSX.writeFile(workbook, 'riverpark-member-import-template.xlsx')
}

export async function parseImportFile(file: File): Promise<MemberImportRowInput[]> {
  const buffer = await file.arrayBuffer()
  const workbook = XLSX.read(buffer, { type: 'array' })
  const sheetName = workbook.SheetNames[0]
  if (!sheetName) throw new Error('The file has no worksheets')

  const sheet = workbook.Sheets[sheetName]
  if (!sheet) throw new Error('Could not read the worksheet')

  const rows = XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1, defval: '' })
  if (rows.length < 2)
    throw new Error('The file must include a header row and at least one member row')

  const headerRow = rows[0]
  if (!Array.isArray(headerRow)) throw new Error('Invalid header row')

  const headers = headerRow.map((h) => String(h ?? ''))
  const map = mapImportHeaderFields(headers)

  if (map.name === undefined || map.phone === undefined) {
    throw new Error('The file must include "Name" and "Telephone number" columns')
  }

  const dataRows = rows.slice(1).filter((row) => {
    if (!Array.isArray(row)) return false
    const name = cellValue(row, map.name)
    const phone = cellValue(row, map.phone)
    return name.length > 0 || phone.length > 0
  })

  if (dataRows.length === 0) throw new Error('No member rows found in the file')
  if (dataRows.length > MEMBER_IMPORT_MAX_ROWS) {
    throw new Error(`Import is limited to ${MEMBER_IMPORT_MAX_ROWS} members per file`)
  }

  return dataRows.map((row) => {
    if (!Array.isArray(row)) throw new Error('Invalid row in file')
    const rawPhone = cellValue(row, map.phone)
    return {
      rowId: crypto.randomUUID(),
      name: cellValue(row, map.name),
      phone: normalizeKenyaPhone(rawPhone),
      email: cellValue(row, map.email) || undefined,
      plotNumber: cellValue(row, map.plotNumber) || undefined,
      boreholeMeterNumber: cellValue(row, map.boreholeMeterNumber) || undefined,
      kplcMeterNumber: cellValue(row, map.kplcMeterNumber) || undefined,
    }
  })
}
